import { UserFilters } from "forms/filters/user";
import { CustomerDataToUpdate } from "../forms/editCustomer";
import {
  CustomerListFilters,
  defaultCustomerFilters,
} from "../forms/filters/customers";
import {
  CustomerCreationData,
  CustomerCreationResponce,
  InviteLinkDisplayData,
  SessionData,
  UserInfoForList,
  UserShortInfo,
  CustomerListData,
  EventCustomerListData,
  CustomerDisplayData,
  BoolResponce,
  UserAdminCreationData,
} from "../types";
import BackendConnect from "./connect";

export interface Rights {
  rang: number;
  permissions: number[];
  permitted_event_types: number[];
  role_id: number[];
  main_group?: number;
  group_ids: number[];
}

export interface GroupData {
  id: number;
  name: string;
  description: string;
  parent_group_id: number;
}

export interface GroupCreationData {
  name: string;
  description: string;
  parent_group_id: number;
  user_id: number;
}

export interface GroupAddUserData {
  user_id: number;
  group_id: number;
  group_role_id: number;
}

export interface SourceData {
  id: number;
  user_id?: number;
  name: string;
  api_key?: string;
}

export interface GroupListData {
  id: number;
  name: string;
  description: string;
  parent_group_id?: number | null;
  date_created: string;
  watcher_internal_role_id?: number;
  hierarchy_level?: number;
  members: number;
}

export interface GroupListDataWithSales extends GroupListData {
  eventCount?: number;
  plan: number;
  fact: number;  
}

export interface RoleCreationData {
  name:string;
  rang: number;
  permissions: number[];
  permitted_events: number[];
}

export interface Role extends RoleCreationData {
  id: number;
}

export interface InviteLinkCreationData {
  name: string;
  role_id: number;
  group_id: number;
  group_role_id: number;
}

export interface InviteLinkData extends InviteLinkCreationData {
  id: number;
}




export class BackendService extends BackendConnect {
  protected static instance: BackendService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendService.instance;
  }

  public async getSessionData(): Promise<SessionData> {
    return await this.get("/userid");
  }

  public async signin(data: { email: string; password: string }) {
    try {
      this.post("/auth/entry", data, "POST", true);
    } catch (e) {
      console.log("Failed auth to display photo:", e)
    }
    return await this.post("/auth", data);
  }

  public async signup(data: { link: string; email: string; password: string, personal: {
    first_name: string;
    phone: string;
  } }) {
    return await this.post("/auth/signup", data);
  }

  public async signout() {
    return await this.get("/auth/signout");
  }

  public async checkInviteLink(link: string): Promise<InviteLinkDisplayData> {
    return await this.get(`/invitelinkdata/${link}`);
  }

  public async requestEmailConfirm(userId: number) {
    return await this.get(`/auth/requestconfirm/${userId}`);
  }

  public async requestEmailConfirmByEmail(email: string) {
    return await this.post(`/auth/requestconfirm/byemail`, { email }, "POST");
  }

  public async requestRecoverPassword(email: string) {
    return await this.post(`/auth/requestnewpassword`, { email }, "POST");
  }

  public async sendEmailToConfirm(token: string) {
    return await this.post(`/auth/confirmemail`, { token }, "POST");
  }

  public async recoverPassword(token: string, newPassword: string) {
    return await this.post(
      `/auth/recoverpassword`,
      { token, newPassword },
      "POST"
    );
  }

  public async getAllUsers(filters: UserFilters): Promise<{ users: UserInfoForList[] }> {
    return this.get("/lists/user/allusers", {...filters});
  }

  public async getAllUsersCount(filters: UserFilters): Promise<{
    count: number;
  }> {
    return this.get("/lists/user/count", {...filters});
  }

  public async getAllGroups(): Promise<{ groups: GroupData[] }> {
    return this.get("/groups/list");
  }

  public async getGroupsList(): Promise<{ groups: GroupListData[] }> {
    return this.get("/groups/all");
  }

  public async getAllUsersShorts(filters: {
    group_id?: number;
  }): Promise<{ users: UserShortInfo[] }> {
    const { group_id } = filters;
    return this.get(group_id ? `/lists/user/shorts/${group_id}` : "/lists/user/shorts");
  }

  public async getAllCustomersShorts(filters: {
    user_id?: number;
    group_id?: number;
  }): Promise<{
    customers: UserShortInfo[];
  }> {
    const { group_id, user_id } = filters;
    return this.get(group_id ? `/customers/shorts/group/${group_id}` : `/customers/shorts/user/${user_id}`);
  }

  public async createNewCustomer(
    data: CustomerCreationData
  ): Promise<{ data: CustomerCreationResponce }> {
    return this.post("/customers/create", data, "POST");
  }

  public async editCustomer(
    data: CustomerDataToUpdate
  ): Promise<BoolResponce> {
    return this.post("/customers/update", data, "POST");
  }

  public async getCustomerEvents(
    customerid: number
  ): Promise<{ events: EventCustomerListData }> {
    return this.get(`/customer/events/${customerid}`);
  }

  public async getCustomerFullData(
    customerid: number
  ): Promise<{ data: CustomerDisplayData }> {
    return this.get(`/customer/fulldata/${customerid}`);
  }

  public async getAllCustomers(
    params: CustomerListFilters = defaultCustomerFilters
  ): Promise<{ customers: CustomerListData[] }> {
    return this.get("/customers/all", { ...params });
  }

  public async getAllCustomersCount(
    params: CustomerListFilters = defaultCustomerFilters
  ): Promise<{ count: number }> {
    return this.get("/customers/count", { ...params });
  }

  public async addCustomerUser(params: {
    customerid: number;
    userid: number;
    roleid: number;
  }): Promise<{ success: boolean; updatedCustomerData: CustomerDisplayData}> {
    return this.post("/customers/adduser", { ...params }, "POST");
  }

  public async isPhoneHold(phone: string): Promise<{ hold: boolean }> {
    return this.get(`/customers/ishold/${phone}`);
  }

  public async getCustomerSources(): Promise<{ sources: SourceData[] }> {
    return this.get("/sources/all");
  }

  public async addCustomerSource(data: {
    name: string;
    userid: number;
    free?: boolean
  }): Promise<{ source: SourceData | null }> {
    return this.post("/sources/new", data, "POST");
  }

  public async deleteCustomerSource(data: {
    api_key: string;
  }): Promise<BoolResponce> {
    return this.post("/sources/delete",data, "POST");
  }

  public async createGroup(data: GroupCreationData): Promise<BoolResponce> {
    return this.post("/groups/create", data, "POST");
  }

  public async deleteGroup(data: { group_id: number; group_transfer_id?: number; }): Promise<BoolResponce> {
    return this.post("/groups/delete", data, "POST");
  }

  public async addUserToGroup(data: GroupAddUserData): Promise<BoolResponce> {
    return this.post("/groups/adduser", data, "POST");
  }

  public async deleteUserFromGroup(data: GroupAddUserData): Promise<BoolResponce> {
    return this.post("/groups/deleteuser", data, "POST");
  }

  public async getRoleList(): Promise<{ roles: Role[]}> {
    return this.get("/roles/all")
  }

  public async getRoleData(roleid: number): Promise<{ roles: Role | null}> {
    return this.get(`/roles/data/${roleid}`)
  }

  public async createRole (data: RoleCreationData): Promise<Role> {
    return this.post(`/roles/create`, {...data}, "POST")
  }

  public async updateRole (data: Role): Promise<Role> {
    return this.post(`/roles/update/${data.id}`,{...data}, "POST")
  }

  public async getCurrentUserRightsData (): Promise<{ rights: Rights | null}> {
    return this.get("/user/rights");
  }

  /* 
    // links
  apiApp.get("/invitelinks/all", requireAuth, getInviteLinksListController);
  apiApp.post("/invitelinks/add", requireAuth, createInviteLinkController);
  apiApp.post("/invitelinks/delete", requireAuth, dropInviteLinkController);  
  // users admin
  apiApp.post("/user/updatestatus", requireAuth, updateUserStatusController);
  apiApp.post("/user/block", requireAuth, blockUserController);  

  */

  public async createUserByAdmin (data: UserAdminCreationData): Promise<{ userId: number }> {
    console.log("Data:", data)
    return this.post("/user/createnew", {data}, "POST");  
  }

  public async getInviteLinksList (): Promise<{ links: InviteLinkData[] }> {
    return this.get("/invitelinks/all")
  }

  public async createInviteLink (data: InviteLinkCreationData): Promise<BoolResponce> {
    return this.post("/invitelinks/add", {...data}, "POST")
  }

  public async deleteInviteLink (link: string): Promise<BoolResponce> {
    return this.post("/invitelinks/delete", {link}, "POST")
  }

  public async updateUserStatusByAdmin (data: {
    user_id: number,
    group_id?: number,
    group_role_id?: number,
    role_id?: number
  }): Promise<BoolResponce> {
    return this.post("/user/updatestatus", {...data}, "POST")
  }

  public async checkDataHold (data: {email?: string; phone?: string }): Promise<{ hold: boolean}> {
    return this.get("/auth/ishold", {...data})   
  }

  public async transferUserActivity(user_id: number, to_id: number): Promise<BoolResponce> {
    return this.post("/user/transferactivity", {user_id, to_id}, "POST")   
  }

  public async blockUser(user_id: number): Promise<BoolResponce> {
    return this.post("/user/block", {user_id}, "POST")   
  }

  public async unBlockUser(user_id: number): Promise<BoolResponce> {
    return this.post("/user/unblock", {user_id}, "POST")   
  }
}

export const backend = BackendService.getInstance();
